import '../static/App.css';
import image5 from "../images/image5.jpg";
import { Helmet } from 'react-helmet';
import { useState } from 'react';
import { ExpandMore, ExpandLess, Description, ContactSupport, LibraryBooks, SupportAgent } from '@mui/icons-material';
import { Box, Typography, Button, Accordion, AccordionSummary, AccordionDetails, useTheme, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';

const Resources = () => {
    const [expanded, setExpanded] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const faqs = [
        {
            question: 'IS THERE A MINIMUM LENGTH OF TIME THAT I CAN REQUEST CARE FOR MY LOVED ONE?',
            answer: 'If you need someone for 1 day, or 365 days, we can take care of you. It\'s in our name and we work to that standard.',
        },
        {
            question: 'WHAT KIND OF PAYMENTS DOES YOUR AGENCY ACCEPT?',
            answer: 'The cost of our services varies depending on the level of care needed. We offer competitive rates and accept a variety of payment methods, including checks and Zelle, as well as working with long-term care insurance.',
        },
        {
            question: 'HOW DOES 24/7 ASSURE CARE ENSURE THE SAFETY OF CLIENTS?',
            answer: 'We take the safety and well-being of our clients very seriously. We have protocols in place to ensure that our caregivers follow proper hygiene and infection control practices, and we regularly monitor the care being provided.',
        },
        {
            question: 'DO YOU PROVIDE WOUND CARE OR ANY MEDICAL SERVICES?',
            answer: "We are a non-medical agency, so we do not provide wound care or medical services directly. However, we can connect you with the right professionals or resources to ensure you receive the care you need.",
        },
        {
            question: 'HOW DOES 24/7 ASSURE CARE HANDLE EMERGENCIES?',
            answer: 'We have protocols in place to handle emergencies, including medical emergencies and severe weather conditions. All our caregivers are trained to respond quickly and appropriately in these situations.',
        },
        {
            question: 'WHAT IF THE CAREGIVER I AM ASSIGNED ISN\'T A GOOD FIT?',
            answer: 'At 24/7 Assure Care, we steer our focus on finding you or your loved one the right caregiver for their needs. If there is ever an issue with a current caregiver, we are more than happy to re-assess needs and try something different.',
        },
        {
            question: 'WHAT AREAS DO YOU SERVE?',
            answer: 'We currently provide services in Cook, DuPage, and Lake counties. If you\'re outside these areas, please contact us as we may still be able to help.',
        },
        {
            question: 'HOW DO I GET STARTED WITH 24/7 ASSURE CARE?',
            answer: 'Getting started is easy! Simply contact us through our website or call us directly. We\'ll schedule a free consultation to assess your needs and create a personalized care plan.',
        },
    ];

    return (
        <div className="page-back">
            <div className="container py-4">
                <Helmet>
                    <title>24/7 AssureCare - Resources & FAQs</title>
                    <meta name="description" content="Comprehensive resources, FAQs, and support materials from 24/7 AssureCare. Everything you need to know about our home care services." />
                    <meta name="keywords" content="home care resources, caregiving FAQs, senior care information, caregiver support, home care questions" />
                </Helmet>

                {/* Hero Section */}
                <Box sx={{ position:'relative', height:isMobile?'300px':'400px', width:'100%', mb:6, borderRadius:3, overflow:'hidden', boxShadow:3 }}>
                    <img src={image5} style={{ width:'100%', height:'100%', objectFit:'cover' }} alt="Caregiver providing compassionate care" />
                    <Box sx={{ position:'absolute', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.4)', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', textAlign:'center', color:'white', p:3 }}>
                    <Typography variant="h2" component="h1" sx={{ fontWeight:'bold', mb:2, fontSize:isMobile?'2rem':'2.8rem', textShadow:'2px 2px 4px rgba(0,0,0,0.7)' }}>
                        Resources & Support
                    </Typography>
                    <Typography variant="h6" sx={{ maxWidth:'800px', textShadow:'1px 1px 3px rgba(0,0,0,0.7)', fontSize:isMobile?'1rem':'1.2rem' }}>
                        At 24/7 AssureCare, we prioritize transparency and believe in keeping our clients fully informed about the care we provide.
                    </Typography>
                    </Box>
                </Box>

                {/* FAQ Section */}
                <Box sx={{ mb:8 }}>
                    <Typography variant="h3" component="h2" sx={{ textAlign:'center', mb:4, color:'rgba(255, 255, 255, 1)', fontWeight:'bold' }}>Frequently Asked Questions</Typography>
                    <Typography variant="body1" sx={{ color: 'white', textAlign:'center', mb:4, maxWidth:'800px', mx:'auto', fontSize:'1.1rem' }}>
                    Find answers to common questions about our services, policies, and care approach. If you don't see what you're looking for, please contact us directly.
                    </Typography>
                    <Box sx={{ maxWidth:'900px', mx:'auto' }}>
                    {faqs.map((faq,index)=>(
                        <Accordion key={index} expanded={expanded===`panel${index}`} onChange={handleChange(`panel${index}`)} sx={{ mb:2, borderRadius:'8px !important', overflow:'hidden', '&:before':{ display:'none' } }}>
                        <AccordionSummary expandIcon={expanded===`panel${index}`?<ExpandLess/>:<ExpandMore/>} sx={{ backgroundColor:expanded===`panel${index}`?'#1e87b0':'#1b8fc9', color:'white', fontWeight:'bold', minHeight:'70px', '&:hover':{ backgroundColor:'#156a9e' } }}>
                            <Typography sx={{ fontWeight:'inherit', fontSize:isMobile?'0.9rem':'1rem' }}>{faq.question}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ backgroundColor:'#f9f9f9', py:3 }}>
                            <Typography>{faq.answer}</Typography>
                        </AccordionDetails>
                        </Accordion>
                    ))}
                    </Box>
                </Box>

                {/* Call to Action Section */}
                <Box sx={{ backgroundColor:'rgba(21,149,160,0.1)', borderRadius:3, p:4, textAlign:'center', mb:4, boxShadow:3 }}>
                    <Typography variant="h4" component="h2" sx={{ mb:2, color:'rgba(0, 0, 0, 1)', fontWeight:'bold' }}>Still Have Questions?</Typography>
                        <Typography variant="body1" sx={{ mb:3, maxWidth:'600px', margin:'0 auto', fontSize:'1.1rem' }}>
                        Our care coordinators are available to answer any questions you may have and help you find the right care solution for your needs.
                    </Typography>
                    <Button component={Link} to="/contact" className='mt-3' variant="contained" size="large" sx={{ backgroundColor:'#1b8fc9', '&:hover':{ color: 'white', backgroundColor:'#156a9e' }, px:4, py:1.5, fontWeight:'bold' }}>
                    Contact Us Today
                    </Button>
                </Box>
            </div>
        </div>
    )

}

export default Resources;